import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';

/**
 * Extracted text line with optional style metadata (bold, heading, font size).
 * Used to detect section headers vs regular text.
 *
 * For PDFs with form widgets (CRA-4-style fillable forms), widgetType/widgetOptions
 * are populated from the PDF annotation layer when a widget is positioned near the line.
 */
export interface TextLine {
  text: string;
  isBold?: boolean;
  isHeading?: boolean;
  fontSize?: number;
  indent?: number;
  widgetType?: 'text' | 'choice' | 'radio' | 'checkbox' | 'date' | 'signature';
  widgetOptions?: string[];
}

/**
 * Service for extracting structured text from Word (.docx) and PDF (.pdf) files.
 * Outputs TextLine[] that can be fed into the free-form parser.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentParserService {

  // ============================================================
  // PUBLIC: Parse any supported file → TextLine[]
  // ============================================================

  parseFile(file: File): Observable<{ lines: TextLine[]; errors: string[] }> {
    const name = file.name.toLowerCase();

    if (name.endsWith('.docx') || name.endsWith('.doc')) {
      return this.parseWord(file);
    }
    if (name.endsWith('.pdf')) {
      return this.parsePdf(file);
    }

    return of({ lines: [], errors: [`Unsupported file type: ${file.name}`] });
  }

  // ============================================================
  // Word (.docx) extraction using mammoth
  // ============================================================

  private parseWord(file: File): Observable<{ lines: TextLine[]; errors: string[] }> {
    return from(this.extractWordText(file)).pipe(
      catchError(error => of({ lines: [] as TextLine[], errors: [`Word parsing failed: ${error.message}`] }))
    );
  }

  private async extractWordText(file: File): Promise<{ lines: TextLine[]; errors: string[] }> {
    const mammothModule = await import('mammoth');
    const mammoth = (mammothModule as any).default || mammothModule;
    const arrayBuffer = await file.arrayBuffer();

    // Get raw HTML to detect bold/heading structure
    const htmlResult = await mammoth.convertToHtml({ arrayBuffer });

    // Also get raw text for fallback
    const textResult = await mammoth.extractRawText({ arrayBuffer });

    const lines: TextLine[] = [];
    const errors: string[] = [];

    if (htmlResult.messages?.length) {
      htmlResult.messages
        .filter((m: any) => m.type === 'warning' || m.type === 'error')
        .forEach((m: any) => errors.push(m.message));
    }

    // Parse HTML to extract text with style info
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlResult.value, 'text/html');

    // Recursive walk so that <ol>/<ul> list items are emitted as separate lines
    // rather than collapsed into the parent's concatenated textContent.
    const processElement = (el: Element, parentIndent: number): void => {
      const tag = el.tagName.toLowerCase();

      // Lists: descend into each child <li>, increasing indent for nesting
      if (tag === 'ol' || tag === 'ul') {
        for (let j = 0; j < el.children.length; j++) {
          processElement(el.children[j], parentIndent + 1);
        }
        return;
      }

      // List items: emit own text (excluding nested lists), then recurse into nested lists
      if (tag === 'li') {
        // Join child text WITHOUT inserting extra spaces — Word docs may have
        // styled inline runs like <span>I</span>nitiation, and join(' ') would
        // wrongly produce "I nitiation". Empty-string join preserves the original
        // word boundaries (the text nodes already carry any meaningful whitespace).
        const ownText = Array.from(el.childNodes)
          .filter(n =>
            n.nodeType === Node.TEXT_NODE ||
            (n.nodeType === Node.ELEMENT_NODE &&
              !['ol', 'ul'].includes((n as Element).tagName.toLowerCase()))
          )
          .map(n => n.textContent || '')
          .join('')
          .replace(/\s+/g, ' ')
          .trim();

        if (ownText) {
          const isBold = el.querySelector('strong, b') !== null;
          lines.push({
            text: ownText,
            isBold,
            isHeading: false,
            fontSize: 12,
            indent: parentIndent
          });
        }

        for (let j = 0; j < el.children.length; j++) {
          const child = el.children[j];
          const childTag = child.tagName.toLowerCase();
          if (childTag === 'ol' || childTag === 'ul') {
            processElement(child, parentIndent);
          }
        }
        return;
      }

      // Regular block element
      const text = (el.textContent || '').trim();
      if (!text) return;

      const isHeading = /^h[1-6]$/.test(tag);
      const isBold = isHeading
        || el.querySelector('strong, b') !== null
        || (el.children.length === 1 && ['strong', 'b'].includes(el.children[0]?.tagName?.toLowerCase()));

      lines.push({
        text,
        isBold,
        isHeading,
        fontSize: isHeading ? 18 : 12,
        indent: parentIndent
      });
    };

    for (let i = 0; i < doc.body.children.length; i++) {
      processElement(doc.body.children[i], 0);
    }

    // If HTML parsing yielded nothing, fall back to raw text
    if (lines.length === 0 && textResult.value) {
      const rawLines = textResult.value.split('\n');
      for (const raw of rawLines) {
        const trimmed = raw.trim();
        if (trimmed) {
          lines.push({ text: trimmed });
        }
      }
    }

    return { lines, errors };
  }

  // ============================================================
  // PDF (.pdf) extraction using pdfjs-dist
  // ============================================================

  private parsePdf(file: File): Observable<{ lines: TextLine[]; errors: string[] }> {
    return from(this.extractPdfText(file)).pipe(
      catchError(error => of({ lines: [] as TextLine[], errors: [`PDF parsing failed: ${error.message}`] }))
    );
  }

  private async extractPdfText(file: File): Promise<{ lines: TextLine[]; errors: string[] }> {
    // Set worker source before calling getDocument
    GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.js';

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: new Uint8Array(arrayBuffer) }).promise;

    interface RawLine {
      text: string;
      isBold: boolean;
      fontSize: number;
      x: number;
      y: number;          // PDF y of line (top of text, lower = further down page)
      page: number;
      widgetType?: TextLine['widgetType'];
      widgetOptions?: string[];
    }
    interface Widget {
      type: NonNullable<TextLine['widgetType']>;
      options?: string[];
      x: number;          // left edge
      y: number;          // top edge in PDF coords (rect[3])
      page: number;
    }

    const rawLines: RawLine[] = [];
    const widgets: Widget[] = [];
    const errors: string[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // --- Extract form widgets (Tx / Ch / Btn) from the annotation layer ---
      // Real PDF form fields. Without them, dropdowns and radios look like plain text.
      // Radio buttons are grouped by fieldName so we can collect their option set.
      try {
        const annotations = await page.getAnnotations();
        const radioGroups = new Map<string, { values: string[]; x: number; y: number }>();

        for (const a of annotations as any[]) {
          if (a.subtype !== 'Widget' || !a.rect) continue;

          let type: Widget['type'] | undefined;
          let options: string[] | undefined;

          switch (a.fieldType) {
            case 'Tx':
              type = 'text';
              break;
            case 'Ch':
              type = 'choice';
              if (Array.isArray(a.options)) {
                options = a.options
                  .map((o: any) => (typeof o === 'string' ? o : (o.displayValue || o.exportValue || o.label || '')))
                  .filter((o: string) => !!o);
              }
              break;
            case 'Btn':
              if (a.checkBox) {
                type = 'checkbox';
              } else if (a.radioButton) {
                // Collect into the group; emit a single composite widget at the end.
                const groupName = a.fieldName || `_anon_${a.id}`;
                const value = a.buttonValue || a.exportValue || a.fieldValue || '';
                const midY = (a.rect[1] + a.rect[3]) / 2;
                const group = radioGroups.get(groupName)
                  || { values: [], x: a.rect[0], y: midY };
                if (value && !group.values.includes(value)) group.values.push(value);
                if (a.rect[0] < group.x) group.x = a.rect[0];
                if (midY > group.y) group.y = midY;
                radioGroups.set(groupName, group);
                continue;
              }
              break;
            case 'Sig':
              type = 'signature';
              break;
          }
          if (!type) continue;

          widgets.push({
            type,
            options,
            x: a.rect[0],
            // Use the middle of the widget rect — PDF text labels have their y at the
            // BASELINE (roughly the middle of the visible row), so anchoring widgets
            // at their middle aligns them with the same-row label.
            y: (a.rect[1] + a.rect[3]) / 2,
            page: pageNum
          });
        }

        // Emit one widget per radio group
        for (const group of radioGroups.values()) {
          widgets.push({
            type: 'radio',
            options: group.values.length > 0 ? group.values : undefined,
            x: group.x,
            y: group.y,
            page: pageNum
          });
        }
      } catch (e: any) {
        errors.push(`Annotation extraction failed on page ${pageNum}: ${e.message}`);
      }

      // --- Extract text, grouped into lines by Y position ---
      const lineMap = new Map<number, { items: any[]; maxFontSize: number }>();

      for (const item of textContent.items) {
        if (!('str' in item) || !item.str.trim()) continue;

        // Round Y to nearest 2px to group items on the same line
        const y = Math.round(item.transform[5] / 2) * 2;
        const fontSize = Math.abs(item.transform[0]) || 12;

        if (!lineMap.has(y)) {
          lineMap.set(y, { items: [], maxFontSize: 0 });
        }
        const line = lineMap.get(y)!;
        line.items.push(item);
        line.maxFontSize = Math.max(line.maxFontSize, fontSize);
      }

      // Sort by Y descending (PDF Y=0 is bottom)
      const sortedLines = Array.from(lineMap.entries())
        .sort(([a], [b]) => b - a);

      for (const [yKey, lineData] of sortedLines) {
        // Sort items left to right by X
        lineData.items.sort((a: any, b: any) => a.transform[4] - b.transform[4]);
        const text = lineData.items.map((item: any) => item.str).join(' ').trim();

        if (!text) continue;

        const fontSize = lineData.maxFontSize;
        const isBold = lineData.items.some((item: any) =>
          item.fontName?.toLowerCase().includes('bold')
        ) || fontSize > 13;
        const x = lineData.items[0]?.transform?.[4] || 0;

        rawLines.push({ text, isBold, fontSize, x, y: yKey, page: pageNum });
      }
    }

    // --- Associate each widget with the closest preceding text line on the same page ---
    // PDFs typically have the label to the LEFT of, or directly ABOVE, the widget.
    // We score each candidate line by Manhattan distance, preferring same-row labels.
    //
    // Lines that are nothing but option markers ("Yes No", "True False") are NOT valid
    // anchors — the widget's label is the question on the left, not the option words next
    // to the widget itself. Skipping them lets the widget attach to the real question line.
    const isOptionMarkerLine = (t: string) =>
      /^\s*(Yes\s+No|No\s+Yes|True\s+False|False\s+True)\s*$/i.test(t);

    for (const w of widgets) {
      let best: { line: RawLine; score: number } | null = null;
      for (const line of rawLines) {
        if (line.page !== w.page) continue;
        if (isOptionMarkerLine(line.text)) continue;
        // Widget must be to the right of or below the label
        const dx = w.x - line.x;
        const dy = line.y - w.y;
        // Same-row: |dy| small (within ~8 PDF units). Below: dy > 0 and small.
        // sameRow tolerance widened to 12 because PDF text baseline can be a few
        // pixels off from the middle of the widget rect.
        const sameRow = Math.abs(dy) < 12;
        const below = dy >= 12 && dy < 24;
        if (!sameRow && !below) continue;
        if (dx < -20) continue;  // widget shouldn't be far to the LEFT of label

        // Score: lower is better. Prefer same-row + closer x.
        const rowPenalty = sameRow ? 0 : (dy * 2);
        const xPenalty = Math.max(0, dx);
        const score = rowPenalty + xPenalty * 0.5 + Math.abs(dx) * 0.1;

        if (!best || score < best.score) {
          best = { line, score };
        }
      }
      if (best && !best.line.widgetType) {
        // Map 'text' widget to 'date' if the label hints at a date
        if (w.type === 'text' && /\bDD\s*\/\s*MM\s*\/\s*YY/i.test(best.line.text)) {
          best.line.widgetType = 'date';
        } else {
          best.line.widgetType = w.type;
        }
        if (w.options) best.line.widgetOptions = w.options;
      }
    }

    // Merge continuation lines: if a line doesn't start a new question/section,
    // append it to the previous line
    const lines: TextLine[] = [];
    const minX = rawLines.length > 0 ? Math.min(...rawLines.map(l => l.x)) : 0;

    // Count unbalanced open parens in a string. Used to detect wrapped-sentence
    // continuations like "(CETA heading level, wherever applicable as per\nRules) ..."
    // where the closing ")" appears on the next line even though it starts uppercase.
    const unbalancedOpenParens = (s: string): number => {
      let depth = 0;
      for (const c of s) {
        if (c === '(') depth++;
        else if (c === ')') depth--;
      }
      return depth;
    };

    for (const raw of rawLines) {
      const isNumbered = /^\d+[\.\)]\s/.test(raw.text);
      // Treat "(a)" / "(b)" / "(i)" / "(ii)" paren markers as new entries too,
      // not just bare "a." or "a)" — otherwise CRA-4-style continuation lines collide.
      const isLettered = /^[a-z][\.\)]\s/i.test(raw.text)
        || /^\(\s*[a-z]+\s*\)/i.test(raw.text);
      const isBullet = /^[-•]\s/.test(raw.text);
      const isIndented = raw.x > minX + 30;

      // Continuation rules:
      //   - prev exists and isn't a heading
      //   - current isn't a new question marker (number / letter / bullet / bold / indented)
      //   - prev doesn't end with sentence-terminating punctuation
      //   - AND either:
      //       * current starts with lowercase (mid-sentence wrap), OR
      //       * prev has unbalanced open parens (closing paren expected on this line)
      const prev = lines.length > 0 ? lines[lines.length - 1] : null;
      const prevEndsWithBreak = prev ? /[?!.:]\s*$/.test(prev.text) : true;
      const startsWithLower = /^[a-z]/.test(raw.text);
      const prevHasUnclosedParen = prev ? unbalancedOpenParens(prev.text) > 0 : false;
      const isContinuation = !!prev
        && !prev.isHeading
        && !isNumbered && !isLettered && !isBullet && !raw.isBold && !isIndented
        && !prevEndsWithBreak
        && (startsWithLower || prevHasUnclosedParen);

      if (isContinuation) {
        prev!.text += ' ' + raw.text;
        // Inherit widget if the continuation row had one (label may wrap onto two lines)
        if (raw.widgetType && !prev!.widgetType) {
          prev!.widgetType = raw.widgetType;
          prev!.widgetOptions = raw.widgetOptions;
        }
      } else {
        lines.push({
          text: raw.text,
          isBold: raw.isBold,
          isHeading: raw.isBold && raw.fontSize > 13,
          fontSize: raw.fontSize,
          indent: isIndented || isLettered || isBullet ? 1 : 0,
          widgetType: raw.widgetType,
          widgetOptions: raw.widgetOptions
        });
      }
    }

    return { lines, errors };
  }
}
